import { map, Observable } from 'rxjs';
import { environment } from '@utility/app.util';

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { Source } from '@sociuu/interfaces';

export type EmvMetricKey =
  | 'click'
  | 'impressions'
  | 'video'
  | 'cpm'
  | 'reactions'
  | 'comments'
  | 'reshares'
  | 'reactions_per_share'
  | 'comments_per_share'
  | 'reshares_per_share';

export interface EmvCurrencyOptionDto {
  code: string;
  title: string;
}

export interface EmvMetricDto {
  value: number | null;
  default_value: number | null;
  is_custom: boolean;
  is_supported: boolean;
  is_currency_bound: boolean;
  disabled_reason: string | null;
}

export interface EmvNetworkDto {
  id: number;
  key: string;
  label: string;
  enabled: boolean;
  auto_select: boolean;
  metrics: Record<EmvMetricKey, EmvMetricDto>;
}

export interface NetworksImpressionsEmvSettingsDto {
  currency: {
    code: string;
    options: EmvCurrencyOptionDto[];
  };
  permissions: {
    can_edit: boolean;
    can_edit_values?: boolean;
    can_edit_currency?: boolean;
    can_disable_network?: boolean;
    can_set_network_default_active?: boolean;
  };
  has_custom_overrides: boolean;
  networks: EmvNetworkDto[];
}

export interface SaveNetworksImpressionsEmvNetworkChangesDto {
  enabled?: boolean;
  auto_select?: boolean;
  click?: number | null;
  impressions?: number | null;
  video?: number | null;
  cpm?: number | null;
  reactions?: number | null;
  comments?: number | null;
  reshares?: number | null;
  reactions_per_share?: number | null;
  comments_per_share?: number | null;
  reshares_per_share?: number | null;
}

export interface SaveNetworksImpressionsEmvSettingsDto {
  currency_code?: string;
  networks?: Record<string, SaveNetworksImpressionsEmvNetworkChangesDto>;
}

@Injectable({
  providedIn: 'root'
})
export class SettingService {
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient) { }

  getSettings(): Observable<any>{
    return this.http.get<Observable<any>>(`${environment.apiUrl}/client/settings/fetch`, this.httpOptions)
  }

  getNotificationSettings(): Observable<any> {
    return this.http.get<Observable<any>>(`${environment.apiUrl}/settings/getNotificationSettings`, this.httpOptions);
  }

  setNotificationSettings(data): Observable<any> {
    return this.http.put(`${environment.apiUrl}/settings/setAdminNotifications`, data, this.httpOptions);
  }

  deleteSource(type: string, id: number): Observable<Source> {
    return this.http.delete<Source>(`${environment.apiUrl}/v2/content-source/delete/${type}/${id}`);
  }

  getNetworksImpressionsEmvSettings(currencyCode?: string): Observable<NetworksImpressionsEmvSettingsDto> {
    let params: HttpParams = new HttpParams();

    if (currencyCode) {
      params = params.set('currency_code', currencyCode);
    }

    return this.http.get<{ data: NetworksImpressionsEmvSettingsDto }>(
      `${environment.apiUrl}/v2/settings/emv`,
      { ...this.httpOptions, params }
    ).pipe(
      map((response: { data: NetworksImpressionsEmvSettingsDto }): NetworksImpressionsEmvSettingsDto => response.data)
    );
  }

  saveNetworksImpressionsEmvSettings(
    data: SaveNetworksImpressionsEmvSettingsDto
  ): Observable<NetworksImpressionsEmvSettingsDto> {
    return this.http.put<{ data: NetworksImpressionsEmvSettingsDto }>(
      `${environment.apiUrl}/v2/settings/emv`,
      data,
      this.httpOptions
    ).pipe(
      map((response: { data: NetworksImpressionsEmvSettingsDto }): NetworksImpressionsEmvSettingsDto => response.data)
    );
  }

  patchNetworksImpressionsEmvNetwork(
    networkKey: string,
    data: SaveNetworksImpressionsEmvNetworkChangesDto
  ): Observable<NetworksImpressionsEmvSettingsDto> {
    const encodedNetworkKey = encodeURIComponent(networkKey);

    return this.http.patch<{ data: NetworksImpressionsEmvSettingsDto }>(
      `${environment.apiUrl}/v2/settings/emv/networks/${encodedNetworkKey}`,
      data,
      this.httpOptions
    ).pipe(
      map((response: { data: NetworksImpressionsEmvSettingsDto }): NetworksImpressionsEmvSettingsDto => response.data)
    );
  }

  resetNetworksImpressionsEmvMetric(
    networkKey: string,
    metricKey: EmvMetricKey,
    currencyCode?: string
  ): Observable<NetworksImpressionsEmvSettingsDto> {
    const encodedNetworkKey = encodeURIComponent(networkKey);
    const encodedMetricKey = encodeURIComponent(metricKey);
    const body = currencyCode ? { currency_code: currencyCode } : {};

    return this.http.post<{ data: NetworksImpressionsEmvSettingsDto }>(
      `${environment.apiUrl}/v2/settings/emv/networks/${encodedNetworkKey}/metrics/${encodedMetricKey}/reset`,
      body,
      this.httpOptions
    ).pipe(
      map((response: { data: NetworksImpressionsEmvSettingsDto }): NetworksImpressionsEmvSettingsDto => response.data)
    );
  }
}
